import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Button,
  Box,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PeopleIcon from "@mui/icons-material/People";
import ImageIcon from "@mui/icons-material/Image";
import { AdvancedOptions } from "./AdvancedOptions";
import { ImageUploadWithPreview } from "./ImageUploadWithPreview";
import { getDefaultSessionCapacity } from "../../../utils/programTimeSlotUtils";

interface SessionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  form: any;
  onChange: (e: any) => void;
  setForm: (form: any) => void;
  editingId: string | null;
  selectedProgramId: string;
  programs: any[];
  staff: Array<{ id: string; email: string; name?: string; role: string; firstName?: string; lastName?: string }>;
  usePerDayDates: boolean;
  setUsePerDayDates: (value: boolean) => void;
  usePerDayTimes: boolean;
  setUsePerDayTimes: (value: boolean) => void;
}

export const SessionDialog = ({
  open,
  onClose,
  onSubmit,
  form,
  onChange,
  setForm,
  editingId,
  selectedProgramId,
  programs,
  staff,
  usePerDayDates,
  setUsePerDayDates,
  usePerDayTimes,
  setUsePerDayTimes,
}: SessionDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={700}>
            {editingId
              ? `Edit Session : ${
                  programs.find((p) => p.id === selectedProgramId)?.title
                }`
              : `Create New Session for ${
                  programs.find((p) => p.id === selectedProgramId)?.title
                }`}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Basic Info */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Basic Information
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Session Name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="e.g., Morning Session"
                required
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Time Slot Type"
                  name="sessionSlotType"
                  value={form.sessionSlotType}
                  onChange={onChange}
                >
                  <MenuItem value="hourly">Hourly (1 hour)</MenuItem>
                  <MenuItem value="two_hour">2 Hours</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="isActive"
                  value={String(form.isActive ?? true)}
                  onChange={onChange}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Stack>
              <TextField
                fullWidth
                type="number"
                label="Session Capacity (per time slot)"
                name="capacity"
                value={form.capacity}
                onChange={onChange}
                inputProps={{ min: 0 }}
                helperText={`Max enrollments per time slot on the selected day. Default when creating: ${getDefaultSessionCapacity(form.sessionSlotType)} for ${form.sessionSlotType === "two_hour" ? "2-hour" : "1-hour"} slots. Optional per-day/per-slot overrides are in Advanced Options.`}
              />
            </Stack>
          </Box>

          {/* Date & Time */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Date & Time
            </Typography>
            {/* {!usePerDayDates && (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start Date & Time"
                  name="startDate"
                  value={form.startDate}
                  onChange={onChange}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="End Date & Time"
                  name="endDate"
                  value={form.endDate}
                  onChange={onChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )} */}
            <Button
              size="small"
              variant={usePerDayDates ? "contained" : "outlined"}
              onClick={() => setUsePerDayDates(!usePerDayDates)}
              sx={{ mt: 2 }}
            >
              {usePerDayDates ? "Using per-day dates" : "Use per-day dates"}
            </Button>
          </Box>

          {/* Session Coach/Employee */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Session Staff
            </Typography>
            <TextField
              select
              fullWidth
              label="Session Coach/Employee (optional)"
              name="sessionCoachOrEmployeeId"
              value={(form as any).sessionCoachOrEmployeeId || ""}
              onChange={onChange}
            >
              <MenuItem value="">None</MenuItem>
              {staff.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {(`${u.firstName} ${u.lastName}`|| u.email) + ` (${u.role})`}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Image Upload */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Session Image
            </Typography>
            <ImageUploadWithPreview
              imageFile={form.imageFile}
              existingImageUrl={form.imageUrl}
              onImageChange={(file) => setForm({ ...form, imageFile: file })}
              label="Session Image"
            />
          </Box>

          {/* Advanced Options */}
          <AdvancedOptions
            form={form}
            setForm={setForm}
            usePerDayTimes={usePerDayTimes}
            setUsePerDayTimes={setUsePerDayTimes}
            usePerDayDates={usePerDayDates}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!form.name || !selectedProgramId}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {editingId ? "Update Session" : "Create Session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
